import { FunctionComponent, useMemo } from "react";
import { useState } from 'react'
import Button from '../../../components/Button';
import { Play } from '../../../assets/svg-icons';
import { Information } from 'react-huge-icons/solid';
import Bench from '../../../assets/images/images.png'
import { rangeUnitToString, singleUnitToString } from "../utils/range-unit-to-string";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CardioExerciseInterval, Query, StrengthExerciseSet, useStartActiveWorkoutMutation, Workout } from "src/graphql";

interface WorkoutSectionProps {
    planId?: string;
    workoutExerciseIndex: number;
    workout: Query['getWorkoutById'];
}

const WorkoutSection: FunctionComponent<WorkoutSectionProps> = ({ workout, planId, workoutExerciseIndex }) => {
    const [watchVideoCollapsed, setWatchVideoCollapsed] = useState(true);
    const [displaySetsCollapsed, setDisplaySetsCollapsed] = useState(true);
    // const activeWorkoutHook = useActiveWorkout();
    const navigate = useNavigate();
    const [startActiveWorkout_pending, setStartActiveWorkout_pending] = useState(false);

    const workoutExercise = useMemo(() => (workout?.exercises || [])[workoutExerciseIndex], [workout, workoutExerciseIndex])
    const [startWorkout, StartWorkoutData] = useStartActiveWorkoutMutation({
        onCompleted() {
            navigate('/active?active=1')
        },
        onError(err) {
            console.error(err);
        }
    })

    const toggleWatchVideo = () => {
        setWatchVideoCollapsed(!watchVideoCollapsed);
        setDisplaySetsCollapsed(true)
    };

    const toggleDisplaySets = () => {
        setDisplaySetsCollapsed(!displaySetsCollapsed);
        setWatchVideoCollapsed(true)

    };

    const StrengthExercise = (props: {
        set: StrengthExerciseSet, idx: number, workoutExerciseId: string
    }) => {

        const { set, idx, workoutExerciseId } = props;

        const { t } = useTranslation();

        let w: number = set.weight?.val!;
        let r: number = set.reps?.max!;

        return (
            <div key={idx} className="gap-x-3">
                <div className="flex gap-4 items-center">
                    <h3 className="text-lg font-bold">{t("SET")} {idx + 1}</h3>
                    {set.rest && <div className='gap-2 items-center flex'>
                        <p className='light'>
                            {t("Rest Time")}
                        </p>
                        <span>
                            {rangeUnitToString(set.rest, t)}
                        </span>
                    </div>}
                </div>
                <div className="space-y-2 text-center">
                    {set.reps && <div className='gap-2 items-center flex'>
                        <div className="flex flex-col">
                            <p className='light'>
                                {t("reps")}
                            </p>
                            <div>
                                {rangeUnitToString(set.reps, t)}
                            </div>
                        </div>
                    </div>}
                    {set.weight && <div className='gap-2 items-center flex'>
                        <div className="flex flex-col">
                            <p className='light'>
                                {t("weight")}
                            </p>
                            <span>
                                {rangeUnitToString(set.weight, t)}
                            </span>
                        </div>
                    </div>}
                </div>
            </div>

        )
    }

    const CardioExercise = (props: {
        set: CardioExerciseInterval, idx: number, workoutExerciseId: string
    }) => {


        const { set, idx, workoutExerciseId } = props;

        const current = Object.keys(set).filter((d) => d !== '__typename').reduce<any>((acc, key) => {
            acc[key] = (set[key as keyof typeof set] as any)?.min || (set[key as keyof typeof set] as any)?.val
            return acc;
        }, {})

        const { t } = useTranslation();

        return (
            <div key={idx} className="gap-x-3">
                <div className="flex gap-2 text-center flex-col">
                    {Boolean(current.dis) && <div className='gap-2 items-center flex'>
                        <div className="flex flex-col">
                            <p className='light'>
                                {t("Distance")}
                            </p>
                            <div>
                                {singleUnitToString(set.distance!, t)}
                            </div>
                        </div>
                    </div>}

                    {Boolean(current.time) && <div className='gap-2 items-center flex'>
                        <div className="flex flex-col">
                            <p className='light'>
                                {t("Duration")}
                            </p>
                            <div>
                                {singleUnitToString(set.duration!, t)}
                            </div>
                        </div>
                    </div>}

                    {Boolean(current.tempo) && <div className='gap-2 items-center flex'>
                        <div className="flex flex-col">
                            <p className='light'>
                                {t("Tempo")}
                            </p>
                            <div>
                                {singleUnitToString(set.tempo!, t)}
                            </div>
                        </div>
                    </div>}

                    {Boolean(current.intens) && <div className='gap-2 items-center flex'>
                        <div className="flex flex-col">
                            <p className='light'>
                                {t("Intensity")}
                            </p>
                            <div>
                                {rangeUnitToString(set.intensity!, t)}
                            </div>
                        </div>
                    </div>}

                    {/* {Boolean(current.thr) && <div className='gap-2 items-center flex'>
                        <div className="flex flex-col">
                            <p className='light'>
                                {t("Target Heart Rate")}
                            </p>
                            <div>
                                {rangeUnitToString(set.thr!, t)}
                            </div>
                        </div>
                    </div>} */}

                    {Boolean(current.angle) && <div className='gap-2 items-center flex'>
                        <div className="flex flex-col">
                            <p className='light'>
                                {t("Angle")}
                            </p>
                            <div>
                                {singleUnitToString(set.angle!, t)}
                            </div>
                        </div>
                    </div>}

                    {Boolean(current.resist) && <div className='gap-2 items-center flex'>
                        <div className="flex flex-col">
                            <p className='light'>
                                {t("Resistance")}
                            </p>
                            <div>
                                {singleUnitToString(set.resistance!, t)}
                            </div>
                        </div>
                    </div>}
                </div>
            </div>

        )
    }


    const { t } = useTranslation();

    if (!workoutExercise) return null;

    return (
        <section>
            <article className=' relative'>
                <div>
                    <div className="flex items-center justify-between">
                        <h1 className='lime'>{workout?.name}</h1>
                        {workoutExercise.notes && <pre>
                            {workoutExercise.notes}
                        </pre>}
                        <div className='flex items-center gap-2'>
                            <a href="#exercises" className="text-blue">
                                רשימת תרגילים
                            </a>
                        </div>
                    </div>

                    <div className="flex items-center -mt-5 justify-between">
                        <h2 className='lime font-bold text-md'>{workoutExercise?.name}</h2>
                        <div className='flex items-center gap-2'>
                            <Button onClick={toggleWatchVideo} sm link>
                                {t("watch_video")}
                            </Button>
                            <button onClick={toggleDisplaySets}>
                                <Information className='h-5 w-5 text-blue' />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mb-5">
                    <div className='flex-row flex gap-2 items-center w-full md:w-auto'>
                        <div>
                            <img src={workout?.trainer?.picture || undefined} alt='avatar' className='avatar object-contain' />
                        </div>
                        <div>
                            <p className='light'>{t("trainer")}</p>
                            <p className=''>{workout?.trainer?.name}</p>
                        </div>
                    </div>
                </div>

                <article>
                    <div className='space-y-6'>
                        {watchVideoCollapsed ? null : <div className=' relative pb-6'>
                            <img src={Bench} alt='Bench' className='w-full md:w-[302px]' />
                            <img src={Play} alt='video' className=' absolute h-8 w-8 top-1/3 translate-x-44 md:translate-x-36 ' />
                        </div>}
                    </div>
                </article>


                <div className='font-Poppins'>
                    <div className='flex text-base flex-col gap-2'>
                        <div className='font-Poppins'>
                            <div className='flex text-base flex-col gap-2'>
                                <div className='flex flex-row gap-2'>
                                    {workoutExercise && workoutExercise.__typename === "StrengthExercise" && (<>
                                        <p className='light'>
                                            {t("Total Sets")}:
                                        </p>
                                        <span>
                                            {workoutExercise?.sets?.length}
                                        </span>
                                    </>)}
                                </div>
                                <div className="space-y-4">
                                    {workoutExercise.__typename === "CardioExercise" ? workoutExercise.intervals?.map((set, idx) => {
                                        return <CardioExercise key={idx} set={set!} idx={idx} workoutExerciseId={workoutExercise.id} />
                                    }) : workoutExercise.__typename === "StrengthExercise" && workoutExercise.sets?.map((set, idx) => {
                                        return <StrengthExercise key={idx} set={set!} idx={idx} workoutExerciseId={workoutExercise.id} />;
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="mt-5">
                            <Button loading={startActiveWorkout_pending} onClick={async () => {
                                setStartActiveWorkout_pending(true)
                                await startWorkout({
                                    variables: {
                                        workoutId: workout?.id!,
                                    }
                                })
                                setStartActiveWorkout_pending(false)
                            }}>{t("start_workout")}</Button>
                        </div>

                    </div>
                </div>
            </article>
        </section>
    );
}

export default WorkoutSection;